import * as React from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import CloseIcon from '@mui/icons-material/Close';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { getfarmer } from '../redux/farmer';
import { API_BASE } from '../constants/config';
import Progress from '../components/Loader';
import { matchFarmerPhotoFile } from '../utils/farmerPhotoMatch';

const statusColor = {
  ready: 'success',
  unmatched: 'warning',
  duplicate: 'error',
  invalid: 'default',
};

export default function ImportFarmerPhotos() {
  const dispatch = useDispatch();
  const farmers = useSelector((state) => state.farmer || []);

  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [selectedFiles, setSelectedFiles] = React.useState([]);
  const [previewRows, setPreviewRows] = React.useState([]);
  const [resultMessage, setResultMessage] = React.useState(null);
  const [resultSeverity, setResultSeverity] = React.useState('success');

  const handleOpen = () => {
    setOpen(true);
    setResultMessage(null);
  };

  const handleClose = () => {
    if (loading) {
      return;
    }
    setOpen(false);
    setSelectedFiles([]);
    setPreviewRows([]);
    setResultMessage(null);
  };

  const rebuildPreview = React.useCallback(
    (files) => {
      setPreviewRows(
        files.map((file) => ({
          file,
          ...matchFarmerPhotoFile(farmers, file.name),
        }))
      );
    },
    [farmers]
  );

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files || []).filter((file) =>
      /\.(jpe?g|png|webp)$/i.test(file.name)
    );
    setSelectedFiles(files);
    rebuildPreview(files);
    setResultMessage(null);
  };

  React.useEffect(() => {
    if (selectedFiles.length > 0) {
      rebuildPreview(selectedFiles);
    }
  }, [farmers, selectedFiles, rebuildPreview]);

  const readyCount = previewRows.filter((row) => row.status === 'ready').length;
  const uploadableFiles = previewRows
    .filter((row) => row.status === 'ready')
    .map((row) => row.file);

  const uploadPhotos = async () => {
    if (uploadableFiles.length === 0) {
      setResultSeverity('warning');
      setResultMessage('Select image files that match at least one farmer.');
      return;
    }

    setLoading(true);
    setResultMessage(null);

    const formData = new FormData();
    uploadableFiles.forEach((file) => {
      formData.append('photos', file);
    });

    try {
      const response = await axios.post(
        `${API_BASE}/api/farmer/bulkassignphotos/`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      );

      if (response.status === 200) {
        const { summary } = response.data;
        dispatch(getfarmer({ data: response.data.farmers }));
        setResultSeverity('success');
        setResultMessage(
          `Assigned ${summary.matched} photo${summary.matched === 1 ? '' : 's'}. ` +
            `${summary.unmatched} unmatched, ${summary.errors} error${summary.errors === 1 ? '' : 's'}.`
        );
        setSelectedFiles([]);
        setPreviewRows([]);
      }
    } catch (error) {
      setResultSeverity('error');
      setResultMessage(
        error.response?.data?.error ||
          error.response?.data?.details ||
          'Failed to upload farmer photos'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Button
        variant="outlined"
        startIcon={<PhotoLibraryIcon />}
        onClick={handleOpen}
        sx={{ marginLeft: 1 }}
      >
        Import Photos
      </Button>

      <Dialog fullWidth maxWidth="md" open={open} onClose={handleClose}>
        <DialogTitle>
          Import farmer photos
          <IconButton
            onClick={handleClose}
            disabled={loading}
            sx={{ float: 'right' }}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <Divider />

        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Select multiple images. Each filename (without extension) must match a farmer&apos;s{' '}
            <strong>ID</strong>, <strong>contact number</strong>, or <strong>ID card number</strong>.
            Example: <code>0241234567.jpg</code>, <code>FMR-GHA-000001.jpg</code>
          </Typography>

          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            onChange={handleFileChange}
          />

          {resultMessage ? (
            <Alert severity={resultSeverity} sx={{ mt: 2 }}>
              {resultMessage}
            </Alert>
          ) : null}

          {previewRows.length > 0 ? (
            <TableContainer component={Paper} sx={{ mt: 2, maxHeight: 360 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>File</TableCell>
                    <TableCell>Lookup key</TableCell>
                    <TableCell>Farmer</TableCell>
                    <TableCell>Matched on</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {previewRows.map((row) => (
                    <TableRow key={row.filename}>
                      <TableCell>{row.filename}</TableCell>
                      <TableCell>{row.key || '—'}</TableCell>
                      <TableCell>{row.farmer?.farmername || '—'}</TableCell>
                      <TableCell>{row.matchField || '—'}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          color={statusColor[row.status] || 'default'}
                          label={row.label}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : null}
        </DialogContent>

        <Divider />

        <DialogActions sx={{ px: 3, py: 2 }}>
          {loading ? (
            <Button variant="outlined" disabled startIcon={<Progress />}>
              Uploading…
            </Button>
          ) : (
            <>
              <Button onClick={handleClose}>Cancel</Button>
              <Button
                variant="contained"
                onClick={uploadPhotos}
                disabled={readyCount === 0}
              >
                Upload {readyCount} matched photo{readyCount === 1 ? '' : 's'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </div>
  );
}
